<template>
  <!-- STEP 1: credentials -->
  <form
    v-if="step == 1"
    class="popup"
    @submit.prevent="doSignIn"
  >
    <slot name="step1_close">
      <nuxt-link
        class="popup__close"
        :to="localePath({name: 'index'})"
      >
        <v-icon icon="ct:cross" size="x-small" />
      </nuxt-link>
    </slot>
    <v-card>
      <v-card-title>
        <h1 class="app__title app__title--h3">{{ t("form.login.title") }}</h1>
      </v-card-title>
      <v-card-text>
        <div id="registration" class="popup__desc">
          {{ t("form.login.subtitle") }}
          <nuxt-link id="register-ambassador" :to="localePath({name: 'register-ambassador'})">
            {{ t("form.login.register") }}
          </nuxt-link>
          {{ t("form.login.or_choice") }}
          <nuxt-link id="register-partner" :to="localePath({name: 'register-partner'})">
            {{ t("form.login.partner") }}
          </nuxt-link>
        </div>
        <div class="app__form">
          <div id="email" class="formItem formItem--1x12">
            <v-text-field
              v-model="email"
              :label="t('form.common.email')"
              type="email"
              :rules="[rules.required, rules.isEmail]"
              :disabled="loggingIn"
              :error-messages="get(errorMessages, 'username')"
              @change="resetErrorMessages('username')"
            />
          </div>
          <div id="password" class="formItem formItem--1x12 formItem--link">
            <auth-password-field
              v-model="password"
              :label="t('form.login.password_label')"
              :validate-strength="false"
              :disabled="loggingIn"
              :error-messages="get(errorMessages, 'password')"
              @change="resetErrorMessages('password')"
            />
            <nuxt-link
              class="link text-right"
              :to="localePath({name: 'forgotten-password'})"
            >
              {{ t("form.login.forgotten-password") }}
            </nuxt-link>
          </div>
          <v-alert
            v-if="has(errorMessages, 'auth')"
            class="vAlert--error formItem formItem--1x12"
            icon="$warning"
          >
            {{ get(errorMessages, 'auth') }}
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          class="mbtn mbtn--green"
          size="x-large"
          type="submit"
          :loading="loggingIn"
        >
          {{ t("form.common.continue") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </form>
  <!-- STEP 2: OTP -->
  <div
    v-if="step == 2"
    class="popup"
  >
    <slot name="step2_close">
      <nuxt-link
        class="popup__close"
        :to="localePath({name: 'index'})"
      >
        <v-icon icon="ct:cross" size="x-small" />
      </nuxt-link>
    </slot>
    <slot name="otp" :phone="phone" :email="email" :token="loginToken">
      <auth-otp-field
        id="auth_otp"
        :phone="phone"
        :email="emailLowercase"
        :token="loginToken"
        :enable-remember-me="true"
        :remember-me-token-validity="rememberMeTokenValidity"
        @verify-otp="otpVerified"
      >
        <div class="link cursor-pointer" @click.prevent="step = 1">
          {{ t("form.login.back") }}
        </div>
      </auth-otp-field>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { get, has } from "lodash-es";

const { $api } = useNuxtApp() as { $api: IApiInstance };
const authStore = useAuthStore();
const config = useRuntimeConfig();
const regionStore = useRegionStore();
const { rules } = useFormValidators();
const localePath = useLocalePath();
const { notify } = useNotification();
const { t } = useI18n();
const appConfig = useAppConfig();

function handleError (e: any) {
  notify({
    title: t("form.common.error"),
    text: e.data?.detail ?? "",
    type: "error"
  });
}

const step = ref<number>(1);

// STEP 1
const email = ref<string>("");
const phone = ref<string>("");
const password = ref<string>("");
const loginToken = ref<string|null>(null);
const errorMessages = ref<Record<string, string[]> | null>(null);
const loggingIn = ref<boolean>(false);
const rememberMeTokenValidity = ref<number>(0);

const _accessTokenCookie = useCookie<string | null>(`auth:${appConfig.appName}:${config.public.region}:access-token`);
const _refreshTokenCookie = useCookie<string | null>(`auth:${appConfig.appName}:${config.public.region}:refresh-token`);

const emailLowercase = computed<string>(() => email.value.toLowerCase());

async function getPhone (): Promise<void> {
  const { data, error } = await $api.auth.userInfo({email: emailLowercase.value});
  if (error.value) {
    handleError(error.value);
    return;
  }
  if (data.value) {
    phone.value = data.value.phone;
  }
}

async function doSignIn (): Promise<void> {
  loggingIn.value = true;
  errorMessages.value = null;
  const { data, error } = await $api.auth.login(emailLowercase.value, password.value, authStore.rememberMeTokenCookie);

  if (error.value) {
    handleError(error.value);
    errorMessages.value = error.value?.data;
  }

  if (data.value?.login_token) {
    loginToken.value = data.value!.login_token;
    authStore.rememberMeTokenCookie = null; // the token is expired
    rememberMeTokenValidity.value = data.value?.remember_me_token_validity ?? 0;
    await getPhone();
    step.value = 2;
  }

  if (!!data.value?.access_token && !!data.value?.refresh_token) {
    authStore.accessToken = data.value!.access_token as string;
    authStore.refreshToken = data.value!.refresh_token as string;
    _refreshTokenCookie.value = data.value!.refresh_token as string;
    _accessTokenCookie.value = data.value!.access_token as string;
    await nextTick(authStore.getSession);
    await otpVerified(true); // Little hack to navigate to dashboard
  }
  loggingIn.value = false;
}

async function setLocaleAndProceed (lang: string, callback: () => void) {
  await regionStore.changeLanguage(lang);
  callback();
}

async function otpVerified (verified: boolean) {
  if (verified) {
    await setLocaleAndProceed(authStore.data!.language!, () => {
      navigateTo(localePath({ name: "profile-dashboard", query: { splash: "true" } })); // TODO - this navigation is really slow - fix it
    });
  }
}

function resetErrorMessages (key: string) {
  if (errorMessages?.value) {
    delete errorMessages.value[key];
  }
}

</script>
